/**
 * 📅 WorkTimelineView — Timeline de trabalho unificado
 *
 * Exibe itens de trabalho em ordem cronológica agrupados por data.
 */

import React, { useMemo } from 'react';
import type { UnifiedWorkItem } from '../types/workItem';
import { WORK_SOURCE_LABELS, WORK_SOURCE_COLORS, WORK_STATUS_LABELS, WORK_STATUS_COLORS } from '../types/workItem';

// ─── Helpers ────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Hoje';
  if (d.toDateString() === yesterday.toDateString()) return 'Ontem';

  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' });
}

function groupByDate(items: UnifiedWorkItem[]): Map<string, UnifiedWorkItem[]> {
  const groups = new Map<string, UnifiedWorkItem[]>();

  const sorted = [...items].sort((a, b) => {
    const da = a.due_date ?? a.created_at;
    const db = b.due_date ?? b.created_at;
    return new Date(da).getTime() - new Date(db).getTime();
  });

  for (const item of sorted) {
    const dateKey = (item.due_date ?? item.created_at).slice(0, 10);
    const list = groups.get(dateKey) ?? [];
    list.push(item);
    groups.set(dateKey, list);
  }

  return groups;
}

// ─── Component ──────────────────────────────────────────────

interface WorkTimelineViewProps {
  items: UnifiedWorkItem[];
}

export const WorkTimelineView: React.FC<WorkTimelineViewProps> = React.memo(({ items }) => {
  const grouped = useMemo(() => groupByDate(items), [items]);

  if (items.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-slate-400 dark:text-slate-500">
        Nenhum item de trabalho encontrado
      </div>
    );
  }

  return (
    <div className="px-4 py-3 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
      {Array.from(grouped.entries()).map(([dateKey, dateItems]) => (
        <div key={dateKey}>
          {/* Date header */}
          <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 bg-[var(--bg-workspace)] py-1">
            <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {formatDate(dateKey)}
            </span>
            <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
          </div>

          {/* Items */}
          <div className="space-y-1.5 pl-4 border-l-2 border-slate-200 dark:border-slate-700 ml-2">
            {dateItems.map(item => {
              const daysUntilDue = item.due_date
                ? Math.ceil((new Date(item.due_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                : null;

              return (
                <div
                  key={item.id}
                  className="relative flex items-start gap-3 rounded-md border border-slate-200 bg-white p-2.5 dark:border-slate-700 dark:bg-slate-800/60"
                >
                  {/* Timeline dot */}
                  <div className="absolute -left-[calc(1rem+5px)] top-3 h-2 w-2 rounded-full bg-slate-400 dark:bg-slate-500" />

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${WORK_SOURCE_COLORS[item.source]}`}>
                        {WORK_SOURCE_LABELS[item.source]}
                      </span>
                      <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${WORK_STATUS_COLORS[item.status]}`}>
                        {WORK_STATUS_LABELS[item.status]}
                      </span>
                      {item.is_overdue && (
                        <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                          ATRASADO
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-medium text-slate-800 dark:text-slate-200 truncate">
                      {item.title}
                    </p>
                    <div className="flex items-center gap-3 mt-1 text-[9px] text-slate-500 dark:text-slate-400">
                      {item.priority && (
                        <span className="font-medium uppercase">{item.priority}</span>
                      )}
                      {daysUntilDue !== null && item.status !== 'completed' && (
                        <span className={daysUntilDue < 0 ? 'text-red-500 font-semibold' : ''}>
                          {daysUntilDue < 0
                            ? `${Math.abs(daysUntilDue)}d atraso`
                            : daysUntilDue === 0
                              ? 'Vence hoje'
                              : `${daysUntilDue}d restantes`}
                        </span>
                      )}
                      {item.source_context?.stage_name && (
                        <span className="truncate">{item.source_context.stage_name}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
});

WorkTimelineView.displayName = 'WorkTimelineView';
